import { takeLatest, call, put, select } from 'redux-saga/effects';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import {
    getQuickScreenEligibilityCheck,
    updateQSWidgetDetails,
} from 'yoda-interfaces/lib/Account/AccountApi';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import ClientSideLoggerAction from 'yoda-site-components/lib/actions/ClientLoggerAction';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import {
    PATCH_HEADER,
    WEB_SYPI_WIDGET_REQUEST,
} from 'yoda-site-components/lib/actionTypes/NativeAppActionTypes';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import {
    GET_QUICKSCREEN_ELIGIBLITY_CHECK,
    GET_QUICKSCREEN_ELIGIBLITY_CHECK_FAILURE,
    GET_QUICKSCREEN_ELIGIBLITY_CHECK_SUCCESS,
    UPDATE_QUICKSCREEN_WIDGET_DETAILS,
    UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS,
    QUICKSCREEN_ANALYTICS_EVENT,
    WEB_GET_REWARD_PROFILE_REQUEST_QS,
    LOAD_SYF_QUICKSCREEN_SCRIPT,
    UPDATE_QS_WIDGET_STATUS,
    RESET_QUICKSCREEN_ELIGIBLITY,
    WEB_OPEN_QUICK_SCREEN_REQUEST,
} from '../actionTypes/SYFActionTypes';
import { seeSYFQuickScreenDetails } from '../helpers/Utils/SYFUtil';
import {
    codesToHideBannerOnClose as defaultCodesToHideBannerOnClose,
    syfResponseCodes as defaultSyfResponseCodes,
    syfResponseCodeWebView,
} from '../common/Constants';
import { selectSyfQuickScreenResponse } from '../selectors/syfQuickScreenSelector';

const context = (state) => state.context;

export function* loadSYFScript(action) {
    const { payload: responseData } = action;
    const contextSelector = yield select(context);
    const isNative = contextSelector?.isNative;
    const quickScreenConfig = contextSelector?.preferences?.quickScreenConfig || {};
    const isQuickScreenEligible = responseData?.eligible;
    const dsaToken = responseData?.offerToken;
    const acceptanceId = responseData?.offerDetails?.acceptanceId;
    const enableAppsSDKQSWidget = contextSelector?.featureFlags?.enableAppsSDKQSWidget || false;
    const renderAppsSDKWidget = isNative && enableAppsSDKQSWidget; // flag to render webScript/appsSDK widget in webview, if false treat it as dotcom widget render
    const isNativeEligibleToRenderBanner =
        isQuickScreenEligible && renderAppsSDKWidget && acceptanceId;
    const isDotcomEligibleToRenderBanner =
        isQuickScreenEligible && !renderAppsSDKWidget && dsaToken;
    const syfQuickScreenSelector = (state) => state.syfQuickScreen;
    const syfQuickScreen = yield select(syfQuickScreenSelector);
    // MCYODA-30125 show the loader if it needs to render mobile
    if (syfQuickScreen.isWidgetLoaded) {
        yield put({
            type: UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS,
            payload: { isWidgetLoaded: false, removePreshellLoader: false },
        });
    }
    /** load SYF-QS Script here */
    if (isDotcomEligibleToRenderBanner) {
        quickScreenConfig.dsaToken = dsaToken;
        yield call(seeSYFQuickScreenDetails, quickScreenConfig);
    }
    // bypass quickscreendetails calls for native app
    if (isNativeEligibleToRenderBanner) {
        yield put({
            type: UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS,
            payload: { isWidgetLoaded: true, removePreshellLoader: true },
        });
    }
    if (isNativeEligibleToRenderBanner) {
        yield put({
            type: QUICKSCREEN_ANALYTICS_EVENT,
            payload: { eventType: 'bannerimpression' },
        });
    }
}

export function* getEligibilityCheckSaga(action) {
    try {
        /* eslint-disable no-param-reassign */
        action.payload = { rewardId: TokenProvider.get('DP_REWARDS_ID'), fromGETQS: true };
        const result = yield call(FactorySaga, getQuickScreenEligibilityCheck, action);
        if (result?.isSuccess) {
            if (!result?.response?.data?.eligible) {
                const qsEligibilityData = {
                    isQsNotEligible: true,
                    lastVerifiedTime: Date.now(),
                };
                LocalStorage.setData('qsEligibilityData', JSON.stringify(qsEligibilityData));
            }
            yield put({
                type: GET_QUICKSCREEN_ELIGIBLITY_CHECK_SUCCESS,
                payload: { data: result?.response?.data },
            });
            yield call(loadSYFScript, { payload: result?.response?.data });
        } else {
            const errorInfo = Array.isArray(result?.response?.data)
                ? result?.response?.data[0]
                : result?.response?.data;
            const payload = {
                error: errorInfo,
            };
            yield put({ type: GET_QUICKSCREEN_ELIGIBLITY_CHECK_FAILURE, payload });
        }
    } catch (err) {
        const payload = {
            error: {
                errorMessage: err.message,
                errorCode: 'EXCEPTION_IN_SYF_QUICKSCREEN_SAGA',
            },
        };
        yield put({ type: GET_QUICKSCREEN_ELIGIBLITY_CHECK_FAILURE, payload });
    }
}

function* updateQSWidgetResponseToCAM(action) {
    try {
        const {
            payload: { applicantStatus },
        } = action;
        const payload = {
            responsePayload: { applicantStatus },
            rewardId: TokenProvider.get('DP_REWARDS_ID'),
        };
        yield put({ type: 'SHOW_GLOBAL_LOADER', payload: true });
        yield call(FactorySaga, updateQSWidgetDetails, { payload });
        yield put({ type: 'SHOW_GLOBAL_LOADER', payload: false });
    } catch (e) {
        // eslint-disable-next-line no-console
        console.info('error updating QS widgetResponseToCAM');
    }
}

function* saveQSWidgetResponse(widgetDetails, codesToHideBannerOnClose) {
    if (codesToHideBannerOnClose.indexOf(widgetDetails?.responseCode) > -1) {
        yield put({
            type: UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS,
            payload: { hideBannerOnClose: true },
        });
    }
}

function* handleWidgetVisibility(isWidgetVisible) {
    const contextSelector = yield select(context);
    const isNativeAndroid = contextSelector?.isNativeAndroid;
    yield put({ type: PATCH_HEADER, payload: { data: { backButton: !isWidgetVisible } } });
    yield put({
        type: UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS,
        payload: { isWidgetVisible },
    });
    if (isNativeAndroid) {
        yield put({
            type: WEB_SYPI_WIDGET_REQUEST,
            payload: { data: { isWidgetVisible } },
        });
    }
}
export function* updateQuickScreenWidgetDetails(action) {
    const {
        payload: { widgetDetails, scriptDetails },
    } = action;
    const syfQuickScreenResponse = yield select(selectSyfQuickScreenResponse);
    const contextSelector = yield select(context);
    const isNative = contextSelector?.isNative;
    const configSyfResponseCodes =
        contextSelector?.preferences?.quickScreenConfig?.configSyfResponseCodes;
    const configToHideBannerOnClose =
        contextSelector?.preferences?.quickScreenConfig?.configToHideBannerOnClose;
    const syfResponseCodes = configSyfResponseCodes || defaultSyfResponseCodes;
    const codesToHideBannerOnClose = configToHideBannerOnClose || defaultCodesToHideBannerOnClose;
    if (widgetDetails && syfQuickScreenResponse?.eligible) {
        if (widgetDetails?.responseCode) {
            // DA => Trigger Adobe analytics for SYF widget response
            const analyticsStatus = syfResponseCodes[widgetDetails?.responseCode]?.analyticsStatus;
            if (analyticsStatus) {
                yield put({
                    type: QUICKSCREEN_ANALYTICS_EVENT,
                    payload: {
                        eventType: analyticsStatus,
                    },
                });
            }

            // update CAM BE about applicantStatus
            const applicantStatus = syfResponseCodes[widgetDetails?.responseCode]?.CAMStatus;
            if (applicantStatus) {
                yield put({ type: UPDATE_QS_WIDGET_STATUS, payload: { applicantStatus } });
            }

            yield call(saveQSWidgetResponse, widgetDetails, codesToHideBannerOnClose);

            if (
                (widgetDetails?.responseCode === '003' &&
                    syfQuickScreenResponse?.hideBannerOnClose) ||
                widgetDetails?.responseCode === '001'
            ) {
                yield put({ type: RESET_QUICKSCREEN_ELIGIBLITY });
            }
            if (widgetDetails?.responseCode === '003') {
                if (isNative) {
                    yield call(handleWidgetVisibility, false);
                }
            }
            // save widget load success
            if (widgetDetails?.responseCode === '000') {
                const payload = { isWidgetLoaded: true, removePreshellLoader: true };
                yield put({ type: UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS, payload });
            } else {
                const quantumSessionID = Cookies.load('QuantumMetricSessionID') || '';
                const accountId = Cookies.load('ACCOUNT_ID') || '';
                const actionName =
                    widgetDetails?.responseCode === '099'
                        ? 'syf_system_error'
                        : 'syf_unhandled_response_code';
                const errorDescription =
                    widgetDetails?.responseCode === '099'
                        ? 'SYF user action failed due to system error'
                        : 'SYF unhandled response code';
                yield put(
                    ClientSideLoggerAction.logError(
                        errorDescription,
                        `actionName: | ${actionName} |responseCode: ${widgetDetails?.responseCode} | user-agent:${window?.navigator?.userAgent} | quantumSessionID:${quantumSessionID} | accountId:${accountId}`
                    )
                );
                if (widgetDetails?.responseCode === '098') {
                    yield put({ type: RESET_QUICKSCREEN_ELIGIBLITY });
                }
            }
        } else if (widgetDetails?.message === 'openWidget') {
            if (isNative) {
                yield call(handleWidgetVisibility, true);
            }
        }
    } else if (scriptDetails) {
        yield put({ type: UPDATE_QUICKSCREEN_WIDGET_DETAILS_SUCCESS, payload: scriptDetails });
    }
}

export function* getUserRewardsProfile(action) {
    const {
        payload: { rewardsId = '', rewardsTier = '' },
    } = action;
    if (rewardsId) {
        TokenProvider.set('DP_REWARDS_ID', rewardsId);
        TokenProvider.set('DP_REWARDS_TIER', rewardsTier);
        yield put({ type: GET_QUICKSCREEN_ELIGIBLITY_CHECK });
    }
}

export function* openWebViewQuickScreenSaga(action) {
    const responseCode = syfResponseCodeWebView[action.payload.status];
    if (responseCode) {
        const payload = { widgetDetails: { responseCode } };
        yield put({ type: UPDATE_QUICKSCREEN_WIDGET_DETAILS, payload });
    }
}

const watchSYFQuickScreenSaga = function* watchSYFQuickScreenSaga() {
    yield takeLatest(WEB_GET_REWARD_PROFILE_REQUEST_QS, getUserRewardsProfile);
    yield takeLatest(WEB_OPEN_QUICK_SCREEN_REQUEST, openWebViewQuickScreenSaga);
    yield takeLatest(GET_QUICKSCREEN_ELIGIBLITY_CHECK, getEligibilityCheckSaga);
    yield takeLatest(UPDATE_QUICKSCREEN_WIDGET_DETAILS, updateQuickScreenWidgetDetails);
    yield takeLatest(UPDATE_QS_WIDGET_STATUS, updateQSWidgetResponseToCAM);
    yield takeLatest(LOAD_SYF_QUICKSCREEN_SCRIPT, loadSYFScript);
};
watchSYFQuickScreenSaga.sagaName = 'watchSYFQuickScreenSaga';

export default watchSYFQuickScreenSaga;
